import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './CrmInbox.css';
import CrmReminder from './CrmReminder';
import './CustomerCrm.css';

export default function CrmInbox({ canReadCrm = true, canReadReports = false }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [snoozing, setSnoozing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef(null);
  const request = useRef(0);
  const mutating = useRef(false);
  const mounted = useRef(false);
  const load = useCallback(async () => {
    if (mutating.current) return;
    const token = ++request.current;
    try {
      const sources = [...(canReadCrm ? ['/crm-inbox'] : []), ...(canReadReports ? ['/reports/notifications'] : [])];
      const results = await Promise.allSettled(sources.map(url => api.get(url)));
      if (mounted.current && token === request.current) {
        setMessages(previous => results.flatMap((result, index) => result.status === 'fulfilled'
          ? (result.value.data.messages || [])
          : previous.filter(message => (message.type === 'report') === (sources[index] === '/reports/notifications')))
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
        setError(results.some(result => result.status === 'rejected') ? 'Unable to refresh part of your inbox. Please try again.' : '');
      }
    } catch (err) {
      if (mounted.current && token === request.current) setError(err.response?.data?.error || 'Unable to refresh CRM inbox.');
    } finally { if (mounted.current && token === request.current) setLoading(false); }
  }, [canReadCrm, canReadReports]);
  useEffect(() => {
    mounted.current = true;
    load();
    const refresh = () => { if (!document.hidden) load(); };
    const timer = setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    window.addEventListener('crm-inbox-changed', refresh);
    return () => {
      mounted.current = false; request.current += 1;
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('crm-inbox-changed', refresh);
    };
  }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const element = dialog.current;
    element.showModal();
    load();
    return () => element.close();
  }, [open, load]);
  const update = async (message, action, destination = false) => {
    if (mutating.current) return;
    mutating.current = true; request.current += 1; setBusy(true); setError('');
    try {
      const isReport = message.type === 'report';
      const { data } = isReport
        ? await api.put(`/reports/notifications/${encodeURIComponent(message.jobId)}/${action}`)
        : await api.put(`/crm-inbox/${message.id}/${action}`, { version: message.version, reminderGeneration: message.reminderGeneration || 0 });
      if (!mounted.current) return;
      // Shared completion also cancels alarms, so refresh the open CRM conversation.
      if (action === 'done' && !isReport) window.dispatchEvent(new Event('crm-reminder-changed'));
      setMessages(rows => action === 'done' ? rows.filter(row => row.id !== message.id)
        : rows.map(row => row.id === message.id ? { ...row, readAt: data.readAt } : row));
      if (destination) {
        setOpen(false);
        navigate(isReport ? `/reports?job=${encodeURIComponent(message.jobId)}` : `/${message.destination}?customerId=${encodeURIComponent(message.customerId)}&tab=crm&messageId=${encodeURIComponent(message.id)}`);
      } else setExpanded(action === 'done' ? null : message.id);
    } catch (err) {
      if (mounted.current) setError(err.response?.data?.error || 'Unable to update the inbox. Please try again.');
    } finally { mutating.current = false; if (mounted.current) { setBusy(false); setLoading(false); } }
  };
  const unread = messages.filter(message => !message.readAt).length;
  return <>
    <button type="button" className="crm-inbox-trigger" title={error || 'CRM Inbox'} aria-label={`CRM Inbox${unread ? `, ${unread} new messages` : ''}${error ? ', refresh failed' : ''}`} onClick={() => setOpen(true)}>
      <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 6 9 7 9-7" /></svg>
      {unread > 0 && <span className="crm-inbox-new" aria-live="polite">New {unread}</span>}
      {error && <span aria-hidden="true">!</span>}
    </button>
    {open && <dialog ref={dialog} className="crm-inbox-modal" aria-labelledby="crm-inbox-title" onCancel={() => setOpen(false)}>
      <header><h2 id="crm-inbox-title">CRM Inbox</h2><button type="button" className="crm-inbox-close" aria-label="Close CRM inbox" onClick={() => setOpen(false)} autoFocus>×</button></header>
      <p className="crm-inbox-help">For customer CRM messages, Done removes a message from everyone's inbox and cancels its existing reminders. Reading and snoozing only change your inbox. Report notifications are private to you; Done only dismisses your notification and keeps the report available until it expires.</p>
      <div className="crm-inbox-toolbar"><span>{unread} new · {messages.length} in inbox</span><button type="button" disabled={busy} onClick={load}>Refresh</button></div>
      {error && <p role="alert" className="crm-inbox-error">{error}</p>}
      <div className="crm-inbox-list" aria-busy={loading}>
        {loading ? <p>Loading inbox...</p> : messages.length === 0 ? <p>Your inbox is empty.</p> : messages.map(message => <article key={message.id} className={`crm-inbox-item${message.readAt ? '' : ' unread'}`}>
          <div className="crm-inbox-item-heading"><strong>{message.customerName}</strong>{!message.readAt && <span className="crm-inbox-new">New</span>}</div>
          {message.reminderAt && <span className="crm-inbox-new">Scheduled reminder</span>}
          <small>{message.authorName} · {new Date(message.createdAt).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })} Eastern</small>
          {message.editedAt && <small className="crm-inbox-edited">Edited by {message.editedBy} · {new Date(message.editedAt).toLocaleString('en-US', { timeZone: 'America/New_York' })} Eastern</small>}
          <p className={`crm-inbox-body${expanded === message.id ? ' expanded' : ''}`}>{message.body}</p>
          <div className="crm-inbox-item-actions">
            <button type="button" disabled={busy} onClick={() => update(message, 'read')}>{message.readAt ? 'Read message' : 'Mark read & expand'}</button>
            <button type="button" disabled={busy} onClick={() => update(message, 'read', true)}>{message.type === 'report' ? 'Open report' : `Open CRM in ${message.destination === 'payments' ? 'Payments' : 'Customers'}`}</button>
            <button type="button" disabled={busy} onClick={() => update(message, 'done')}>Done</button>
            {message.type !== 'report' && <button type="button" disabled={busy} onClick={() => setSnoozing(message)}>Snooze</button>}
          </div>
        </article>)}
      </div>
    </dialog>}
    {snoozing && <div className="customer-crm"><CrmReminder message={snoozing} snooze onClose={() => setSnoozing(null)} onSaved={() => { setMessages(rows => rows.filter(row => row.id !== snoozing.id)); setExpanded(null); }} /></div>}
  </>;
}
